import { CHART_COLORS_WARM as COLORS } from '@/lib/constants';

// ─── Types ───────────────────────────────────────────────────────────

export interface ChartConfig {
  x_key?: string;
  y_key?: string;
  label_key?: string;
  value_key?: string;
  lat_key?: string;
  lng_key?: string;
  name_key?: string;
  popup_keys?: string[];
  marker_color_key?: string;
  color?: string;
  colors?: string[];
  color_by_key?: string;
  x_label?: string;
  y_label?: string;
  number_decimals?: number;
  number_prefix?: string;
  number_suffix?: string;
  show_values?: boolean;
  show_legend?: boolean;
  show_grid?: boolean;
  sort_data?: string;
  stacked?: boolean;
  group_key?: string;
  min_value?: number;
  max_value?: number;
  height?: number;
  show_total?: boolean;
  total_annotation?: string;
  percentage?: boolean;
}

export interface ChartSpec {
  chart_type: 'bar' | 'horizontal_bar' | 'line' | 'pie' | 'map';
  title: string;
  subtitle?: string;
  data: Record<string, unknown>[];
  /** Signed URL to fetch full data when dataset was too large for inline. */
  data_url?: string;
  /** Total data points (may be larger than data.length if offloaded). */
  total_data_points?: number;
  config: ChartConfig;
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Coerce any value to a number, or return null. */
export function toNumber(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return isNaN(v) ? null : v;
  if (typeof v === 'string') {
    const cleaned = v.trim().replace(/,/g, '');
    if (!cleaned) return null;
    const n = Number(cleaned);
    return isNaN(n) ? null : n;
  }
  return null;
}

/** Coerce all numeric columns in data to actual numbers. */
export function coerceData(data: Record<string, unknown>[], numericKeys: Set<string>): Record<string, unknown>[] {
  return data.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(row)) {
      out[k] = numericKeys.has(k) ? toNumber(v) : v;
    }
    return out;
  });
}

/**
 * Determine which keys contain numeric data by inspecting actual values.
 * Purely data-driven — never relies on config keys (which may be swapped).
 */
export function getNumericKeys(data: Record<string, unknown>[]): Set<string> {
  const keys = new Set<string>();
  if (data.length === 0) return keys;

  for (const k of Object.keys(data[0])) {
    const sample = data.find((r) => r[k] != null)?.[k];
    if (sample != null && typeof sample !== 'boolean' && toNumber(sample) !== null) {
      keys.add(k);
    }
  }

  return keys;
}

export function formatValue(v: unknown, config: ChartConfig): string {
  if (v == null) return '';
  const num = toNumber(v);
  if (num === null) return String(v);
  const decimals = config.number_decimals ?? 0;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${config.number_prefix || ''}${formatted}${config.number_suffix || ''}`;
}

export function getColors(config: ChartConfig): string[] {
  return config.colors && config.colors.length > 0 ? config.colors : COLORS;
}

/** Resolve key with fallback to first/second column. */
export function resolveKey(
  config: ChartConfig,
  field: 'x' | 'y' | 'label' | 'value',
  data: Record<string, unknown>[],
): string {
  const keys = data.length > 0 ? Object.keys(data[0]) : [];
  if (field === 'x') return config.x_key || keys[0] || 'x';
  if (field === 'y') return config.y_key || keys[1] || 'y';
  if (field === 'label') return config.label_key || keys[0] || 'label';
  return config.value_key || keys[1] || 'value';
}

/**
 * Resolve category (label) and value (numeric) keys for bar charts.
 * Auto-swaps x_key/y_key when the agent puts them in the wrong order
 * (e.g. numeric in x_key, string in y_key for horizontal_bar).
 */
export function resolveBarKeys(
  config: ChartConfig,
  data: Record<string, unknown>[],
): { categoryKey: string; valueKey: string } {
  const rawX = resolveKey(config, 'x', data);
  const rawY = resolveKey(config, 'y', data);

  if (data.length === 0) return { categoryKey: rawX, valueKey: rawY };

  const xIsNumeric = toNumber(data[0][rawX]) !== null;
  const yIsNumeric = toNumber(data[0][rawY]) !== null;

  // If x is numeric and y is not, the agent swapped them — auto-fix
  if (xIsNumeric && !yIsNumeric) {
    return { categoryKey: rawY, valueKey: rawX };
  }

  return { categoryKey: rawX, valueKey: rawY };
}
